/**
 * ChallengesPage — First-Class Challenge Discovery
 * ──────────────────────────────────────────────────
 * "5 new challenges in Make It Sound Good"
 *
 * v2 Changes:
 * - Search + zone filters now read from PlatformContext (persist Map ↔ Explore ↔ here)
 * - Replaced local search form + zone chips with UnifiedSearchBar component
 * - Sort (unanswered/newest/recommended) stays local — challenge-specific
 * - hasFilters checks both platform and local state
 *
 * Place at: src/pages/ChallengesPage.jsx
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Target, Clock, Users, ArrowRight, Search, X,
  Package, Palette, Music, Utensils, BookOpen, Stethoscope,
  Cpu, TrendingUp, Sparkles, Eye, GitFork, Zap,
  MessageSquare, MapPin, ChevronRight, Filter,
  Plus, ExternalLink, RefreshCw, Layers,
} from 'lucide-react';
import { cn } from '../utils/cn';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { usePlatform } from '../context/PlatformContext';
import { ZONES, getProjectZone } from '../config/zones';
import UnifiedSearchBar from '../components/UnifiedSearchBar';

// ============================================================================
// CONSTANTS
// ============================================================================

const ZONE_LIST = Object.values(ZONES);

const ZONE_ICONS = {
  build: Package,
  beautiful: Palette,
  sound: Music,
  taste: Utensils,
  story: BookOpen,
  heal: Stethoscope,
  smart: Cpu,
  sell: TrendingUp,
};

const SORT_OPTIONS = [
  { id: 'unanswered', label: 'Unanswered', icon: Target, description: 'Need responses' },
  { id: 'newest',     label: 'Newest',     icon: Clock,  description: 'Recently posted' },
  { id: 'recommended', label: 'For You',   icon: Sparkles, description: 'Based on your skills' },
];

// ============================================================================
// HELPERS
// ============================================================================

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

function getZoneForProject(project) {
  if (!project) return null;
  try {
    return getProjectZone(project);
  } catch {
    return null;
  }
}

function getZoneForChallenge(challenge) {
  // Try the direct zone field first (from creative_identity_api)
  if (challenge.zone && ZONES[challenge.zone]) return ZONES[challenge.zone];
  // Fall back to deriving from the parent project
  if (challenge.project) return getZoneForProject(challenge.project);
  return null;
}

// ============================================================================
// CHALLENGE CARD
// ============================================================================

const ChallengeCard = ({ challenge, onRespond, onViewOnMap, isVisible }) => {
  const responseCount = challenge.response_count || challenge.responseCount || 0;
  const isUnanswered  = responseCount === 0;
  const title         = challenge.challenge_title || challenge.challengeTitle || challenge.title || challenge.prompt || 'Untitled Challenge';
  const description   = challenge.challenge_description || challenge.challengeDescription || challenge.description;
  const skills        = challenge.skills_needed || challenge.skillsNeeded || [];
  const project       = challenge.project;
  const projectImage  = project?.image_url || project?.imageUrl || project?.cover_image_url;
  const projectName   = project?.name;
  const zone          = getZoneForChallenge(challenge);
  const ZoneIcon      = zone ? (ZONE_ICONS[zone.id] || Target) : Target;
  const matchReasons  = challenge.matchReasons;
  const created       = challenge.created_at || challenge.createdAt;
  const requester     = challenge.requester?.username || challenge.requesterUsername ||
                        challenge.requester_username || challenge.author?.username || 'someone';

  return (
    <div
      className={cn(
        'group relative bg-zinc-900/70 border rounded-2xl overflow-hidden transition-all duration-500',
        'hover:border-amber-500/40 hover:bg-zinc-900 hover:shadow-lg hover:shadow-amber-500/5',
        isUnanswered ? 'border-amber-500/20' : 'border-zinc-800/60',
      )}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(16px)',
        transition: 'opacity 0.5s ease, transform 0.5s ease',
      }}
    >
      {/* Amber accent line */}
      <div
        className="h-0.5"
        style={{
          background: zone
            ? `linear-gradient(90deg, ${zone.color}60, #f59e0b40, transparent)`
            : 'linear-gradient(90deg, #f59e0b60, #f59e0b20, transparent)',
        }}
      />

      <div className="flex">
        {/* Project thumbnail strip — left side */}
        <div className="w-20 flex-shrink-0 relative overflow-hidden bg-zinc-800/40">
          {projectImage ? (
            <img
              src={projectImage}
              alt={projectName || ''}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center"
              style={{ background: zone ? `linear-gradient(135deg, ${zone.color}15, transparent)` : undefined }}
            >
              <ZoneIcon
                className="w-6 h-6"
                style={{ color: zone?.color || '#f59e0b', opacity: 0.35 }}
              />
            </div>
          )}
          {/* Gradient fade */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-zinc-900/80" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 p-4">
          {/* Top row: zone + response status */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 flex-wrap">
              {zone && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold"
                  style={{ backgroundColor: `${zone.color}15`, color: zone.color }}
                >
                  <ZoneIcon className="w-2.5 h-2.5" />
                  {zone.shortLabel}
                </span>
              )}
              {challenge.challengeType && (
                <span className="px-2 py-0.5 rounded-md text-[10px] bg-zinc-800/80 text-zinc-500 font-medium">
                  {challenge.challengeType.replace(/_/g, ' ')}
                </span>
              )}
            </div>

            {isUnanswered ? (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold bg-amber-500/12 text-amber-400 border border-amber-500/20">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                Be the first!
              </span>
            ) : (
              <span className="text-[11px] text-zinc-500 font-medium">
                {responseCount} response{responseCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* Challenge title */}
          <h3 className="text-sm font-bold text-white leading-snug mb-1.5 line-clamp-2 group-hover:text-amber-200/90 transition-colors">
            &ldquo;{title}&rdquo;
          </h3>

          {/* Parent project reference */}
          {projectName && (
            <p className="text-[11px] text-zinc-500 mb-2 truncate">
              on <span className="text-zinc-400 font-medium">{projectName}</span>
              {requester && <> by <span className="text-zinc-400">@{requester}</span></>}
            </p>
          )}

          {/* Description */}
          {description && (
            <p className="text-[12px] text-zinc-400 leading-relaxed line-clamp-2 mb-2.5">
              {description}
            </p>
          )}

          {/* Skills needed */}
          {skills.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {skills.slice(0, 5).map((skill, i) => {
                const isMatch = matchReasons?.skillMatches?.includes(
                  typeof skill === 'string' ? skill.toLowerCase() : skill,
                );
                return (
                  <span
                    key={i}
                    className={cn(
                      'text-[10px] px-2 py-0.5 rounded-md border font-medium',
                      isMatch
                        ? 'bg-purple-500/12 text-purple-300 border-purple-500/25'
                        : 'bg-zinc-800/70 text-zinc-500 border-zinc-700/40',
                    )}
                  >
                    {typeof skill === 'string' ? skill : skill.name || skill}
                  </span>
                );
              })}
              {skills.length > 5 && (
                <span className="text-[10px] text-zinc-600 font-medium">+{skills.length - 5}</span>
              )}
            </div>
          )}

          {/* Match reasons */}
          {matchReasons && (matchReasons.zoneMatch || (matchReasons.skillMatches && matchReasons.skillMatches.length > 0)) && (
            <div className="flex items-center gap-1.5 mb-3">
              <Sparkles className="w-3 h-3 text-purple-400" />
              <span className="text-[10px] text-purple-400 font-semibold">
                {matchReasons.zoneMatch && matchReasons.skillMatches?.length > 0
                  ? `In your zone + ${matchReasons.skillMatches.length} skill match${matchReasons.skillMatches.length > 1 ? 'es' : ''}`
                  : matchReasons.zoneMatch
                    ? 'In your zone'
                    : `${matchReasons.skillMatches.length} skill match${matchReasons.skillMatches.length > 1 ? 'es' : ''}`}
              </span>
            </div>
          )}

          {/* Footer: time + actions */}
          <div className="flex items-center justify-between pt-3 border-t border-zinc-800/40">
            <span className="text-[10px] text-zinc-600">{timeAgo(created)}</span>
            <div className="flex items-center gap-2">
              {onViewOnMap && (
                <button
                  onClick={(e) => { e.stopPropagation(); onViewOnMap(challenge); }}
                  className="text-[10px] text-zinc-500 hover:text-white transition-colors font-medium"
                >
                  View on Map
                </button>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); onRespond?.(challenge); }}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-[11px] font-bold transition-all',
                  isUnanswered
                    ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30 hover:bg-amber-500/25 shadow-sm shadow-amber-500/10'
                    : 'bg-zinc-800 text-zinc-300 border border-zinc-700/50 hover:bg-zinc-700 hover:text-white',
                )}
              >
                Respond
                <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// STATS BAR
// ============================================================================

const StatsBar = ({ total, unansweredCount, loading }) => (
  <div className="flex items-center gap-4 flex-wrap">
    <div className="flex items-center gap-2">
      <div className="w-9 h-9 rounded-xl bg-amber-500/12 flex items-center justify-center">
        <Target className="w-4.5 h-4.5 text-amber-400" />
      </div>
      <div>
        <h1 className="text-lg font-bold text-white leading-none">Open Challenges</h1>
        <p className="text-[11px] text-zinc-500 mt-0.5">
          {loading ? 'Loading...' : `${total} challenge${total !== 1 ? 's' : ''} waiting for your help`}
        </p>
      </div>
    </div>

    {unansweredCount > 0 && !loading && (
      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20">
        <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
        <span className="text-[11px] font-bold text-amber-400">
          {unansweredCount} need{unansweredCount === 1 ? 's' : ''} a response
        </span>
      </div>
    )}
  </div>
);

// ============================================================================
// LOADING SKELETON
// ============================================================================

const LoadingSkeleton = () => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
    {[0, 1, 2, 3, 4, 5].map(i => (
      <div key={i} className="bg-zinc-900/50 border border-zinc-800/40 rounded-2xl overflow-hidden animate-pulse">
        <div className="h-0.5 bg-zinc-800" />
        <div className="flex">
          <div className="w-20 h-36 bg-zinc-800/60 flex-shrink-0" />
          <div className="flex-1 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <div className="h-5 w-16 rounded-md bg-zinc-800" />
              <div className="h-5 w-20 rounded-full bg-zinc-800 ml-auto" />
            </div>
            <div className="h-4 w-3/4 rounded bg-zinc-800" />
            <div className="h-3 w-1/2 rounded bg-zinc-800/60" />
            <div className="h-3 w-full rounded bg-zinc-800/40" />
            <div className="flex gap-1.5">
              <div className="h-5 w-14 rounded-md bg-zinc-800" />
              <div className="h-5 w-14 rounded-md bg-zinc-800" />
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

// ============================================================================
// EMPTY STATE
// ============================================================================

const EmptyState = ({ hasFilters, onClearFilters }) => (
  <div className="flex flex-col items-center justify-center py-20 text-center">
    <div className="w-16 h-16 rounded-2xl bg-zinc-900 border border-zinc-800 flex items-center justify-center mb-5">
      <Target className="w-8 h-8 text-zinc-700" />
    </div>
    <h3 className="text-base font-bold text-white mb-2">No open challenges</h3>
    <p className="text-sm text-zinc-500 mb-6 max-w-xs leading-relaxed">
      {hasFilters
        ? 'Try removing some filters to see more challenges.'
        : 'Be the first to post a challenge on one of your projects. Ask for help with fonts, colors, code, recipes \u2014 anything.'}
    </p>
    <div className="flex items-center gap-3">
      {hasFilters && (
        <button
          onClick={onClearFilters}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-zinc-800 text-zinc-300 text-sm font-medium hover:bg-zinc-700 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
          Clear filters
        </button>
      )}
      <a
        href="/dashboard?create-challenge"
        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-500/15 text-amber-400 border border-amber-500/30 text-sm font-semibold hover:bg-amber-500/25 transition-colors"
      >
        <Plus className="w-3.5 h-3.5" />
        Post a Challenge
      </a>
    </div>
  </div>
);

// ============================================================================
// MAIN PAGE
// ============================================================================

const ChallengesPage = () => {
  const navigate     = useNavigate();
  const { user }     = useAuth();
  const scrollRef    = useRef(null);
  const platform     = usePlatform();

  // Read search from PlatformContext (shared with Map + Explore)
  const searchQuery = platform.search.debouncedQuery;

  // Read zone filter from PlatformContext — use first active zone or ''
  const activeZone = platform.filters.activeZones.length > 0
    ? platform.filters.activeZones[0]
    : '';

  const [challenges, setChallenges]     = useState([]);
  const [loading, setLoading]           = useState(true);
  const [sort, setSort]                 = useState('unanswered');
  const [total, setTotal]               = useState(0);
  const [visibleCards, setVisibleCards]  = useState(new Set());
  const cardRefs = useRef([]);

  // ── Intersection observer for staggered card reveal ─────────────────
  useEffect(() => {
    if (challenges.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const idx = Number(entry.target.dataset.index);
            if (!isNaN(idx)) {
              setVisibleCards(prev => new Set([...prev, idx]));
            }
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -5% 0px' },
    );

    cardRefs.current.forEach(el => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [challenges.length]);

  // ── Data fetching ───────────────────────────────────────────────────
  const fetchChallenges = useCallback(async () => {
    setLoading(true);
    setVisibleCards(new Set());

    try {
      let res;

      if (sort === 'recommended') {
        // Use the personalized endpoint
        const params = new URLSearchParams();
        params.set('limit', '30');
        res = await api.get(`/challenges/recommended?${params.toString()}`);
      } else {
        // Use the browse endpoint
        const params = new URLSearchParams();
        if (activeZone) params.set('zone', activeZone);
        if (searchQuery) params.set('q', searchQuery);
        params.set('sort', sort === 'unanswered' ? 'recent' : sort);
        params.set('limit', '30');
        res = await api.get(`/challenges?${params.toString()}`);
      }

      let all = res.data.challenges || res.data || [];

      // Client-side zone filtering for recommended (it doesn't support zone param)
      if (sort === 'recommended' && activeZone) {
        all = all.filter(c => {
          const z = getZoneForChallenge(c);
          return z && z.id === activeZone;
        });
      }

      // Client-side sort for unanswered-first
      if (sort === 'unanswered') {
        all.sort((a, b) => {
          const aResp = a.response_count || a.responseCount || 0;
          const bResp = b.response_count || b.responseCount || 0;
          if (aResp === 0 && bResp > 0) return -1;
          if (bResp === 0 && aResp > 0) return 1;
          return new Date(b.created_at || b.createdAt || 0) - new Date(a.created_at || a.createdAt || 0);
        });
      }

      setChallenges(all);
      setTotal(res.data.total || all.length);
    } catch (err) {
      console.error('Failed to load challenges:', err);
      setChallenges([]);
      setTotal(0);
    }
    setLoading(false);
  }, [activeZone, sort, searchQuery]);

  useEffect(() => {
    fetchChallenges();
  }, [fetchChallenges]);

  // ── Derived state ──────────────────────────────────────────────────
  const unansweredCount = useMemo(() =>
    challenges.filter(c => (c.response_count || c.responseCount || 0) === 0).length,
    [challenges],
  );

  const hasFilters = activeZone || searchQuery || platform.filters.activeFilterCount > 0;

  // ── Handlers ───────────────────────────────────────────────────────
  const handleRespond = (challenge) => {
    const pid = challenge.project_id || challenge.projectId || challenge.project?.id;
    const cid = challenge.id;
    navigate(`/dashboard?challenge=${cid}${pid ? `&project=${pid}` : ''}`);
  };

  const handleViewOnMap = (challenge) => {
    const pid = challenge.project_id || challenge.projectId || challenge.project?.id;
    const cid = challenge.id;
    navigate(`/dashboard?${pid ? `focus=${pid}&` : ''}challenge=${cid}`);
  };

  const handleSearch = (e) => {
    e.preventDefault();
  };

  const clearFilters = () => {
    platform.search.clear();
    platform.filters.clearAllFilters();
  };

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <div ref={scrollRef} className="min-h-screen bg-zinc-950 overflow-y-auto">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">

        {/* ── Sticky header ──────────────────────────────────────────── */}
        <div className="sticky top-0 z-30 bg-zinc-950/90 backdrop-blur-xl pt-3 pb-3 -mx-4 px-4 sm:-mx-6 sm:px-6 border-b border-zinc-800/30">
          {/* Stats + post button */}
          <div className="flex items-center justify-between mb-3">
            <StatsBar total={total} unansweredCount={unansweredCount} loading={loading} />
            <a
              href="/dashboard?create-challenge"
              className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-amber-500/12 text-amber-400 border border-amber-500/25 text-[12px] font-bold hover:bg-amber-500/20 transition-all"
            >
              <Plus className="w-3.5 h-3.5" />
              Post Challenge
            </a>
          </div>

          {/* Unified Search + Filters (shared with Map + Explore) */}
          <div className="mb-2">
            <UnifiedSearchBar
              variant="page"
              onResultClick={(project) => navigate(`/dashboard?focus=${project.id}`)}
              onChallengeClick={(c) => {
                const pid = c.project_id || c.projectId || c.project?.id;
                navigate(`/dashboard?challenge=${c.id}${pid ? `&project=${pid}` : ''}`);
              }}
              onUserClick={(u) => navigate(`/profile/${u.id}`)}
            />
          </div>

          {/* Sort tabs (challenge-specific — these stay local) */}
          <div className="flex items-center gap-1 p-0.5 bg-zinc-800/30 rounded-lg w-fit">
            {SORT_OPTIONS.map(opt => {
              const Icon = opt.icon;
              const isActive = sort === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => setSort(opt.id)}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold transition-all',
                    isActive
                      ? 'bg-zinc-700 text-white shadow-sm'
                      : 'text-zinc-500 hover:text-zinc-300',
                  )}
                >
                  <Icon className="w-3 h-3" />
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Content ─────────────────────────────────────────────────── */}
        <div className="py-5">
          {loading ? (
            <LoadingSkeleton />
          ) : challenges.length === 0 ? (
            <EmptyState hasFilters={hasFilters} onClearFilters={clearFilters} />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {challenges.map((challenge, i) => (
                <div
                  key={challenge.id}
                  ref={el => { cardRefs.current[i] = el; }}
                  data-index={i}
                >
                  <ChallengeCard
                    challenge={challenge}
                    onRespond={handleRespond}
                    onViewOnMap={handleViewOnMap}
                    isVisible={visibleCards.has(i)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bottom padding for mobile nav */}
        <div className="h-20 md:h-8" />
      </div>

      {/* Inline styles */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default ChallengesPage;